import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useEffect, useCallback } from 'preact/hooks';
import { Loader2 } from 'lucide-preact';
import { useAuth } from '@/auth/useAuth';
import { useWebLoader } from '@/hooks/useWebLoader';
import { useLandingPageChat } from '@/hooks/useLandingPageChat';
import { LandingPageSidebar } from '@/components/webs/landingPage/LandingPageSidebar';
import { LandingPageEditorPanel } from '@/components/webs/landingPage/LandingPageEditorPanel';
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview';
import { WebsterChatInput } from '@/components/webster/WebsterChatInput';
import { ExternalModeNotice } from '@/components/webs/overlays/WebsterBuilderShared';
export function LandingPageBuilder({ clientCompanyId, workspaceId, campaignId, webId, assetId, onReadyForVersionChange, onLandingPageEstablished, onLandingPageRebuilt, onDraftUpdatedAtChange, onGeneratingChange, }) {
    const { web } = useWebLoader(workspaceId, webId);
    const { userProfile } = useAuth();
    const { messages, generating, hasGenerated, landingPage, setLandingPage, html, loading, context, contextLoading, thankYouHtml, thankYouGenerating, loadThankYouDraft, updateThankYouSettings, regenerateThankYou, suggestThankYouGeneration, canGenerateThankYou, chatReady, generationError, thankYouGenerationError, startGeneration, sendMessage, retryGeneration, loadPreview, updateSettings, updateContext, updatePageMeta, turnState, turnUserId, turnUserName, } = useLandingPageChat({ clientCompanyId, workspaceId, campaignId, webId, onRebuilt: onLandingPageRebuilt });
    // Multi-user turn lock. When another user is mid-turn on the shared chat
    // session, lock this user's input and attribute the wait. Falls through to
    // null when the turn is idle or this user is the holder (their own input is
    // already disabled by `generating`).
    // Fallback to a generic label if turnUserName is missing — without it the
    // input wouldn't lock at all (lockedByUserName goes null), defeating the
    // turn-lock UX when the holder's name happens to be empty on the server side.
    const lockedByUserName = (turnState === 'Active' && turnUserId && turnUserId !== userProfile?.Id)
        ? (turnUserName || 'Another user')
        : null;
    // Local settings state
    const [selectedLayout, setSelectedLayout] = useState(landingPage?.Settings?.Layout || 'Hero Flow');
    const [showAgenda, setShowAgenda] = useState(landingPage?.Settings?.ShowAgenda ?? true);
    const [showSpeakerBios, setShowSpeakerBios] = useState(landingPage?.Settings?.ShowSpeakerBios ?? true);
    const [showCountdown, setShowCountdown] = useState(landingPage?.Settings?.ShowCountdown ?? true);
    const [showDuration, setShowDuration] = useState(landingPage?.Settings?.ShowDuration ?? true);
    // Always defaults true — independent of company-level TrackingEnabled (that
    // gate is enforced server-side at event ingestion, not at LP generation).
    const [includeTracking, setIncludeTracking] = useState(landingPage?.Settings?.IncludeTracking ?? true);
    const [formFields, setFormFields] = useState(landingPage?.Settings?.FormFields ?? null);
    const [showPreview, setShowPreview] = useState(!!landingPage);
    useEffect(() => {
        if (generating || hasGenerated)
            setShowPreview(true);
    }, [generating, hasGenerated]);
    const [localContext, setLocalContext] = useState(null);
    const [localThankYouSettings, setLocalThankYouSettings] = useState(null);
    const [localPageMeta, setLocalPageMeta] = useState(null);
    useEffect(() => { if (context)
        setLocalContext(context); }, [context]);
    useEffect(() => { if (landingPage?.PageMeta)
        setLocalPageMeta(landingPage.PageMeta); }, [landingPage?.PageMeta]);
    useEffect(() => {
        if (landingPage?.ThankYouSettings)
            setLocalThankYouSettings(landingPage.ThankYouSettings);
    }, [landingPage?.ThankYouSettings]);
    useEffect(() => {
        if (landingPage?.Settings) {
            setSelectedLayout(landingPage.Settings.Layout);
            setShowAgenda(landingPage.Settings.ShowAgenda);
            setShowSpeakerBios(landingPage.Settings.ShowSpeakerBios);
            setShowCountdown(landingPage.Settings.ShowCountdown);
            setShowDuration(landingPage.Settings.ShowDuration);
            // Defensive ?? true for pre-feature LPs whose Settings doc
            // pre-dates the IncludeTracking field.
            setIncludeTracking(landingPage.Settings.IncludeTracking ?? true);
            setFormFields(landingPage.Settings.FormFields ?? null);
        }
    }, [landingPage?.Id]);
    useEffect(() => {
        if (web && !((web.Type || '').toLowerCase().includes('online') || !!web.OnlineEvent) && !landingPage?.Settings) {
            setShowAgenda(false);
            setShowSpeakerBios(false);
            setShowCountdown(false);
            setShowDuration(false);
        }
    }, [web, landingPage?.Settings]);
    useEffect(() => {
        if (hasGenerated && !html && !generating)
            loadPreview();
    }, [hasGenerated, html, generating, loadPreview]);
    // Report to parent whether LP is ready for "Save as Version"
    // Ready = has been generated AND thank you page is done (canGenerateThankYou === false)
    useEffect(() => {
        onReadyForVersionChange?.(hasGenerated && !canGenerateThankYou);
    }, [hasGenerated, canGenerateThankYou, onReadyForVersionChange]);
    // Push LP-entity DraftUpdatedAt up to the parent — it owns the Save gate
    // and for LPs this is the authoritative draft timestamp.
    useEffect(() => {
        onDraftUpdatedAtChange?.(landingPage?.DraftUpdatedAt ?? null);
    }, [landingPage?.DraftUpdatedAt, onDraftUpdatedAtChange]);
    // Push generating up so the Save-as-Version button can be disabled while Webster is
    // working. For LPs `generating` already spans the initial generation and chat rebuilds.
    useEffect(() => {
        onGeneratingChange?.(generating);
    }, [generating, onGeneratingChange]);
    // Notify parent when the LP entity first becomes available so it can
    // re-fetch the WebAsset (its LandingPageId is denormalized on the asset
    // and is what gates the "Save as Version" button).
    useEffect(() => {
        if (landingPage?.Id)
            onLandingPageEstablished?.();
    }, [landingPage?.Id, onLandingPageEstablished]);
    useEffect(() => {
        if (hasGenerated && !thankYouHtml && !generating && !thankYouGenerating && landingPage?.ThankYouGenerationStatus === 'Completed') {
            loadThankYouDraft();
        }
    }, [hasGenerated, thankYouHtml, generating, thankYouGenerating, loadThankYouDraft, landingPage?.ThankYouGenerationStatus]);
    const handleSend = async (message) => {
        if (!hasGenerated) {
            // Flip to the rocket/preview surface immediately so the user gets visual
            // feedback the moment they click Submit. The useEffect below also sets this
            // once chat.generating propagates, but doing it synchronously here means we
            // don't depend on state-update timing or extension-init races inside the
            // RichTextBoxes still mounted in the setup sidebar.
            setShowPreview(true);
            const request = {
                Layout: selectedLayout,
                ShowAgenda: showAgenda,
                ShowSpeakerBios: showSpeakerBios,
                ShowCountdown: showCountdown,
                ShowDuration: showDuration,
                IncludeTracking: includeTracking,
                Context: localContext ?? undefined,
                ThankYouSettings: localThankYouSettings ?? undefined,
                PageMeta: localPageMeta ?? undefined,
                AssetId: assetId,
            };
            await startGeneration(request, message || undefined);
        }
        else if (message) {
            await sendMessage(message);
        }
    };
    const handleToggleSetting = (field, value) => {
        switch (field) {
            case 'ShowAgenda':
                setShowAgenda(value);
                break;
            case 'ShowSpeakerBios':
                setShowSpeakerBios(value);
                break;
            case 'ShowCountdown':
                setShowCountdown(value);
                break;
            case 'ShowDuration':
                setShowDuration(value);
                break;
            case 'IncludeTracking':
                setIncludeTracking(value);
                break;
        }
        if (landingPage)
            updateSettings({ ETag: landingPage.ETag, [field]: value });
    };
    const handleLayoutChange = (layout) => {
        setSelectedLayout(layout);
        if (landingPage)
            updateSettings({ ETag: landingPage.ETag, Layout: layout });
    };
    const handleFormFieldsChange = (canonicalKeys) => {
        setFormFields(canonicalKeys);
        if (landingPage)
            updateSettings({ ETag: landingPage.ETag, FormFields: canonicalKeys });
    };
    const handleContextChange = useCallback((field, value) => {
        setLocalContext(prev => prev ? { ...prev, [field]: value } : { [field]: value });
        // Persist post-generation edits so a refresh doesn't lose the user's input.
        // Pre-generation, the value flows through the Generate request body so
        // there's nothing to save until landingPage exists.
        if (landingPage)
            updateContext({ ETag: landingPage.ETag, [field]: value });
    }, [landingPage, updateContext]);
    const handleThankYouSettingsChange = useCallback((field, value) => {
        setLocalThankYouSettings(prev => prev ? { ...prev, [field]: value } : { [field]: value });
        if (landingPage)
            updateThankYouSettings({ ETag: landingPage.ETag, [field]: value });
    }, [landingPage, updateThankYouSettings]);
    const handlePageMetaChange = useCallback((next) => {
        // Update local state synchronously so the controlled inputs in
        // PageMetaSection don't visibly revert while the PATCH is in flight.
        setLocalPageMeta(next);
        // Post-generation: persist immediately. Pre-generation: localPageMeta
        // is carried into the Generate request body so the values land on the
        // freshly-created LandingPage entity.
        if (landingPage) {
            updatePageMeta({
                ETag: landingPage.ETag,
                OgImageAlt: next.OgImageAlt,
                MetaDescription: next.MetaDescription,
            });
        }
    }, [landingPage, updatePageMeta]);
    const handleLandingPageUpdate = useCallback((updated) => {
        setLandingPage(updated);
    }, [setLandingPage]);
    // Wait for all critical data before showing the builder.
    // For existing LPs (hasGenerated), wait for HTML to load too so we don't
    // flash empty states. For new LPs (no generation yet), just need metadata + web.
    const isInitializing = loading || !web || (hasGenerated && !html && !generating);
    if (isInitializing) {
        return (_jsx("div", { className: "h-full flex items-center justify-center", children: _jsxs("div", { className: "flex flex-col items-center gap-3", children: [_jsx(Loader2, { className: "w-6 h-6 text-primary-600 animate-spin" }), _jsx("span", { className: "text-sm text-slate-500 dark:text-slate-400", children: "Loading..." })] }) }));
    }
    if (landingPage?.Mode === 'External') {
        return _jsx(ExternalModeNotice, { url: landingPage.DeployedUrl });
    }
    return (_jsx("div", { className: "h-full flex overflow-hidden", children: !showPreview ? (_jsxs("div", { className: "flex-1 flex flex-col", children: [_jsx("div", { className: "flex-1 overflow-y-auto", children: _jsx("div", { className: "max-w-3xl mx-auto", children: _jsx(LandingPageSidebar, { context: localContext, contextLoading: contextLoading, selectedLayout: selectedLayout, onSelectLayout: handleLayoutChange, showAgenda: showAgenda, showSpeakerBios: showSpeakerBios, showCountdown: showCountdown, showDuration: showDuration, includeTracking: includeTracking, onToggleSetting: handleToggleSetting, formFields: formFields, onFormFieldsChange: handleFormFieldsChange, onContextChange: handleContextChange, thankYouSettings: localThankYouSettings, onThankYouSettingsChange: handleThankYouSettingsChange, pageMeta: localPageMeta, onPageMetaChange: handlePageMetaChange, onLandingPageUpdate: handleLandingPageUpdate, workspaceId: workspaceId, webId: webId, disabled: generating, webType: web.Type }) }) }), _jsx(WebsterChatInput, { onSend: handleSend, disabled: generating, lockedByUserName: lockedByUserName, buttonLabel: "Generate", allowEmpty: true, placeholder: "Describe your ideal landing page, or just click Generate to get started..." })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex-1 min-w-0", children: _jsx(HtmlPreview, { html: html, thankYouHtml: thankYouHtml, loading: generating && !html && !thankYouGenerating, thankYouGenerating: thankYouGenerating, generationError: generationError, thankYouGenerationError: thankYouGenerationError, onRetry: retryGeneration, canGenerateThankYou: canGenerateThankYou, onGenerateThankYou: () => regenerateThankYou() }) }), _jsx(LandingPageEditorPanel, { context: localContext, contextLoading: contextLoading, selectedLayout: selectedLayout, showAgenda: showAgenda, showSpeakerBios: showSpeakerBios, showCountdown: showCountdown, showDuration: showDuration, includeTracking: includeTracking, onSelectLayout: handleLayoutChange, onToggleSetting: handleToggleSetting, formFields: formFields, onFormFieldsChange: handleFormFieldsChange, onContextChange: handleContextChange, thankYouSettings: localThankYouSettings, onThankYouSettingsChange: handleThankYouSettingsChange, pageMeta: localPageMeta, onPageMetaChange: handlePageMetaChange, chat: { messages, generating }, onSend: handleSend, disabled: generating, chatDisabled: !chatReady, lockedByUserName: lockedByUserName, suggestThankYouGeneration: suggestThankYouGeneration, onGenerateThankYou: () => regenerateThankYou(), webType: web.Type })] })) }));
}
